import React, {useState} from 'react';
import {ComposedModal, ModalHeader, ModalBody, ModalFooter, TextInput, Button} from '@carbon/react';

export interface DeleteFileModalProps {
    isOpen: boolean;
    fileName: string;
    uploadedAt: string;
    isLoading?: boolean;
    error?: string;
    onClose: () => void;
    onDelete: () => void;
}

export const DeleteFileModal: React.FC<DeleteFileModalProps> = ({
    isOpen,
    fileName,
    uploadedAt,
    isLoading = false,
    error,
    onClose,
    onDelete,
}) => {
    const [confirmationText, setConfirmationText] = useState('');
    const hasTyped = confirmationText.length > 0;
    const isConfirmed = confirmationText === fileName;
    const showMismatch = hasTyped && !isConfirmed;

    const handleClose = () => {
        setConfirmationText('');
        onClose();
    };

    const handleSubmit = () => {
        if (!isConfirmed || isLoading) return;
        onDelete();
    };

    const helperText = isConfirmed
        ? '✓ File name matches'
        : showMismatch
            ? undefined
            : 'Enter file name exactly as shown above';

    return (
        <ComposedModal
            open={isOpen}
            onClose={handleClose}
            size="sm"
        >
            <ModalHeader title="Delete file?" />
            <ModalBody>
                <p style={{marginBottom: '1rem', fontSize: '0.875rem', color: '#161616'}}>
                    This action will permanently delete this file and its associated data.
                </p>
                <p style={{marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 600, color: '#161616'}}>
                    {fileName}
                </p>
                <p style={{marginBottom: '1.5rem', fontSize: '0.875rem', color: '#525252'}}>
                    Uploaded {uploadedAt}
                </p>
                <TextInput
                    id="confirm-deletion"
                    labelText="Enter file name to confirm deletion"
                    value={confirmationText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setConfirmationText(e.target.value)
                    }
                    placeholder={fileName}
                    invalid={showMismatch}
                    invalidText="File name does not match"
                    helperText={helperText}
                    disabled={isLoading}
                />
                {error && (
                    <p style={{marginTop: '0.5rem', fontSize: '0.75rem', color: '#da1e28'}}>
                        {error}
                    </p>
                )}
            </ModalBody>
            <ModalFooter style={{justifyContent: 'space-between'}}>
                <Button kind="ghost" onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button
                    kind={isConfirmed ? 'danger' : 'secondary'}
                    onClick={handleSubmit}
                    disabled={!isConfirmed || isLoading}
                >
                    {isLoading ? 'Deleting...' : 'Delete File'}
                </Button>
            </ModalFooter>
        </ComposedModal>
    );
};
